import React, {useState, useCallback} from "react";
import {observer} from "mobx-react";
import * as sharedUtils from "../../serverClientShared/sharedUtils";
import {modalActionTypeEnum, itemTypeEnum,} from "../../enums";
import GeneralDialog from "./GeneralDialog";
import {useStores} from "../../Hooks";
import LimitedTextField from "../Shared/LimitedTextField";
import {procedureTypeEnum} from "../../serverClientShared/sharedEnums";
import Utility from "../../Utility";
import ConditionalComponent from "../Shared/ConditionalComponent";

const CreateRenameModal = observer(() => {
    const {modalStore, generalStore, projectStore, editorStore} = useStores();
    const [nameIsDuplicate, setNameIsDuplicate] = useState(false);

    const {modalData, projectToBeChanged, folderToBeChanged, procedureToBeChanged, itemToBeChanged} = modalStore;
    const isDigitalTwin = modalData.itemType === itemTypeEnum.PROCEDURE && modalData.procedureType === procedureTypeEnum.FUSION;
    const itemName = generalStore.translate(modalData.itemType === itemTypeEnum.PROCEDURE ? (isDigitalTwin ? 'DigitalTwin' : 'InteractiveFlow') : modalData.itemType || ""); // modalItemTypeEnum
    const itemNameUpper = sharedUtils.strToCapital(itemName);
    const modalActionType = generalStore.translate(modalData.actionType || ""); // modalActionTypeEnum
    const title = modalData.actionType === modalActionTypeEnum.CREATE
        ? generalStore.translate('createNewItemSentence1_beforeItem') + ' ' + itemNameUpper + ' ' + generalStore.translate('createNewItemSentence2_afterItem')
        : generalStore.translate('renameItemSentence1_beforeItem') + ' ' + itemNameUpper + ' ' + generalStore.translate('renameItemSentence2_afterItem');
    const modalBodyText = modalData.bodyText;
    const additionalContent = modalData.additionalContent || null;
    const importButton = modalData.importButton || null;
    const additionalClassName = `${modalData.additionalClassName} ${title === `Create New Interactive flow  `  ? 'medium-dialog' : ''}`;
    const placeholder = modalData.placeholder;
    const name = modalStore.modalData.name;
    const isAddNew = modalStore.modalData.isAddNew;
    const trimmedName = name && name.trim();
    const validCharsRegex = /^[A-Za-z0-9 ,.\(\)\[\]:;"!?\/&*+\-_=©™®@]*$/;
    const isNameContainInvalidChar = name && !validCharsRegex.test(name)
    function clearForm() {
        modalStore.modalData.name = '';
    }

    function onEnter() {
        generalStore.forceStopDataPoll = true;

        if (modalData.itemType !== itemTypeEnum.TAG && modalData.actionType !== modalActionTypeEnum.RENAME) {
            if (!(isAddNew && isDigitalTwin && !!modalStore.modalData.name)) {
                modalStore.modalData.name = '';
            }
            return;
        }
        let defaultName = '';
        switch (modalData.itemType) {
            case itemTypeEnum.PROJECT:
                defaultName = projectToBeChanged.ProjectName;
                break;

            case itemTypeEnum.FOLDER:
                defaultName = folderToBeChanged.FolderName;
                break;

            case itemTypeEnum.PROCEDURE:
                defaultName = procedureToBeChanged.ProcedureName;
                break;

            case itemTypeEnum.TAG:
                defaultName = modalData.tagName;
                break;

            case itemTypeEnum.MEDIA_FOLDER:
                defaultName = itemToBeChanged.FolderName;
                break;

            case itemTypeEnum.FILE:
                defaultName = itemToBeChanged.FileName;
                break;

            default:
                return;

        }
        modalStore.modalData.name = defaultName;
    }

    function closeModal() {
        generalStore.forceStopDataPoll = false;
        if (modalStore.modalData.toggleMenu) {
            modalStore.modalData.toggleMenu(null);
        }
        clearForm();
        modalStore.isCreateRenameModalOpen = false;
    }

    const checkIsDuplicate = async trimmed => {
        if (!!trimmed) {
            const isDuplicate = await projectStore.checkSameNameProcedure(trimmed);
            setNameIsDuplicate(isDuplicate);
        }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(Utility.debounce(checkIsDuplicate), [modalStore.modalData]);

    function change(event) {
        const updatedName = event.target.value;
        if (isDigitalTwin) {
            const trimmed = updatedName && updatedName.trim();
            debouncedSearch(trimmed);
        }
        modalStore.modalData.name = event.target.value;
    }

    async function confirm() {
        const name = modalStore.modalData.name.trim();
        if (!name) {
            return;
        }

        if (isDigitalTwin && isAddNew) {
            modalStore.isCreateRenameModalOpen = false;
            return modalStore.isSelectDigitalTwinUploadModalOpen = true
        }
        generalStore.isLoaderOpen = true;

        const {doMainAction} = modalStore.modalData;
        const onError = () => {
            generalStore.isLoaderOpen = false;
        }

        try {
            const success = await doMainAction(name);
            if (success) {
                generalStore.isLoaderOpen = false;
                editorStore.refreshTrigger = !editorStore.refreshTrigger;
                closeModal();
            } else {
                onError();
            }
        } catch (e) {
            console.error(e);
            onError();
        }
    }

    return (
        <GeneralDialog
            openControl='isCreateRenameModalOpen'
            className={`${modalData.headerClassName ? '' : 'blue-dialog'} ${modalData.itemType === itemTypeEnum.PROCEDURE ? additionalClassName : ''} `}
            onEnter={onEnter}
            specialOnClose={closeModal}
            headerClassName={modalData.headerClassName}
            title={modalData.headerClassName ? title : null}
            body={
                <div>
                    <ConditionalComponent if={!modalData.headerClassName}>
                        <div style={{marginBottom: "40px", fontWeight: "700", fontSize:"24px" ,color: "var(--main-normal-color)", display: "flex", justifyContent: "center"}}>{generalStore.translate(title).toUpperCase()}</div>
                        <span className="dialog-description ">{modalBodyText}</span> </ConditionalComponent>
                    <LimitedTextField
                        className='name-input interactive-flow-title-input'
                        variant='outlined'
                        autoFocus
                        placeholder={placeholder}
                        value={modalStore.modalData.name}
                        onChange={change}
                        originalValue={modalStore.modalData.name}
                        limit={500}
                        error={!!trimmedName && (nameIsDuplicate || isNameContainInvalidChar)}
                        helperText={!!trimmedName && nameIsDuplicate ? generalStore.translate('duplicateName') : !!trimmedName && isNameContainInvalidChar ? generalStore.translate('invalidCharactersInName') : ""}
                        inputProps={{ 'data-testid': 'create-rename-input' }}
                    />
                    <ConditionalComponent if={!modalData.headerClassName}><span className="dialog-description">{additionalContent}</span></ConditionalComponent>
                </div>
            }
            isConfirmDisabled={!trimmedName || nameIsDuplicate ||  isNameContainInvalidChar}
            onConfirm={confirm}
            confirmText={title === `Create New Interactive flow  ` ? 'Create' : isAddNew ? generalStore.translate('next') : modalActionType}
            preserveDataOnClose
            buttonsClassName={`${modalData.itemType === itemTypeEnum.MEDIA_FOLDER ? 'center-buttons-media' : ''}`}
            buttonClass={`${title === `Create New Interactive flow  `? 'interactive-flow' : ''}`}
            importButton={title === `Create New Interactive flow  `  ? importButton : null }
        />
    );
});

export default CreateRenameModal;
